import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

interface CookieConsentContextType {
  isModalVisible: boolean;
  showModal: () => void;
  hideModal: () => void;
  hasAcceptedCookies: boolean;
  acceptCookies: () => void;
  rejectCookies: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasAcceptedCookies, setHasAcceptedCookies] = useState(false);

  useEffect(() => {
    // Check localStorage for existing consent
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent === 'accepted') {
      setHasAcceptedCookies(true);
    }

    // Listen for cookie modal show events
    const handleShowCookieModal = () => setIsModalVisible(true);
    document.addEventListener('showCookieModal', handleShowCookieModal);

    return () => {
      document.removeEventListener('showCookieModal', handleShowCookieModal);
    };
  }, []);

  const showModal = () => setIsModalVisible(true);
  const hideModal = () => setIsModalVisible(false);

  const acceptCookies = () => {
    setHasAcceptedCookies(true);
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('framerCookiesConsentMode', JSON.stringify({ analytics: true, marketing: true }));
  };

  const rejectCookies = () => {
    setHasAcceptedCookies(false);
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('framerCookiesConsentMode', JSON.stringify({ analytics: false, marketing: false }));
  };

  return (
    <CookieConsentContext.Provider value={{
      isModalVisible,
      showModal,
      hideModal,
      hasAcceptedCookies,
      acceptCookies,
      rejectCookies
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};